import React, { useMemo } from "react";
import { User, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent } from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { useParams } from "react-router-dom";
import { useUnitWiseConsumers } from "@features/onboarding/utility-setup/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface ConfirmUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const ConfirmUpdateModal: React.FC<ConfirmUpdateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const { code } = useParams<{ code: string }>();
  const decodedUnitCode = useMemo(() => decodeURIComponent(code || ""), [code]);
  const remoteUtilityId = useRemoteUtilityId();

  const { data: consumersListResponse, isLoading } = useUnitWiseConsumers({
    unitCode: decodedUnitCode,
    remoteUtilityId,
    isCurrent: 1,
    enabled: isOpen,
  });

  const currentConsumer = consumersListResponse?.result;

  const name = currentConsumer?.name || "N/A";
  const accountNo =
    currentConsumer?.consumer_no || currentConsumer?.consumerNo || "N/A";
  const email = currentConsumer?.email || "N/A";

  const initials =
    name !== "N/A"
      ? name
          .split(" ")
          .map((n: string) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2)
      : "C";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md p-6 rounded-2xl bg-white space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <User className="w-4 h-4" />
          </div>
          <h2 className="text-base font-bold text-gray-900">Update Consumer</h2>
        </div>

        {/* Warning Alert Box */}
        <div className="bg-amber-50/80 border border-amber-200/80 rounded-xl p-4 flex gap-3 text-amber-800">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h4 className="font-bold text-amber-900 text-xs sm:text-sm">
              Consumer already assigned
            </h4>
            <p className="text-xs text-amber-800/90 leading-relaxed mt-0.5">
              This unit already has an active consumer. Updating will replace
              the current assignment.
            </p>
          </div>
        </div>

        {/* Current Consumer Section */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500">
            Current consumer
          </p>

          <div className="border border-gray-200/80 rounded-xl p-3.5 flex items-center justify-between bg-white shadow-2xs">
            {isLoading ? (
              <div className="text-xs text-gray-500 py-2">
                Loading current consumer...
              </div>
            ) : currentConsumer ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold text-sm flex items-center justify-center shrink-0">
                    {initials}
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">{name}</h4>
                    <p className="text-xs text-gray-500">
                      {accountNo} · {email}
                    </p>
                  </div>
                </div>

                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-100 inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Active
                </span>
              </>
            ) : (
              <div className="text-xs text-gray-500 py-2">
                No active consumer assigned.
              </div>
            )}
          </div>
        </div>

        {/* Question Text */}
        <p className="text-xs sm:text-sm font-medium text-gray-700 pt-1">
          Do you still want to update the consumer for this unit?
        </p>

        {/* Footer Buttons */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-700 h-9 cursor-pointer"
          >
            Cancel
          </Button>

          <Button
            onClick={onConfirm}
            className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 h-9 rounded-lg cursor-pointer"
          >
            Yes, Update Consumer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmUpdateModal;
